import { useRef, forwardRef } from 'react';
import styled from 'styled-components/macro';
import ProductPin from './product-pin';
import useTranslations from '../utils/language-provider';
import { BuildingProductsAlternatives } from '../../interfaces/buildings';
import { BuildingType } from '../../config/buildings';

type AlternativeProductsPinProps = {
    alternativeProducts: BuildingProductsAlternatives;
    currentAltProduct: number;
    onSelectAltProduct: (index: number) => void;
    style?: React.CSSProperties;
    className?: string;
    showNewFlag?: boolean;
    buildingType: BuildingType;
};

const AlternativeGridContent = styled.div`
    padding: 5px 10px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 5px;
    transition: transform 300ms ease-in-out;
    background: ${props=> props.theme.colors.primary80};
`;

const AlternativeItemBase = styled.div<{ active: boolean }>`
    cursor: pointer;
    padding: 2px 3px;
    background:${(props) => (props.active ? `linear-gradient(90deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primaryLightContrast} 100%)` : 'transparent')} ;
    transition: background-color 200ms ease-in;
    white-space: nowrap;
    flex-shrink: 0;
    text-align: center;

    &:hover {
        background: linear-gradient(90deg, ${(props) => props.theme.colors.primary} 0%, ${(props) => props.theme.colors.primaryLightContrast} 100%);
    }

    @media ${(props) => props.theme.screens.lg} {
        padding: 2px 4px;
        font-size: ${(props) => props.theme.typography.md};
    }
`;

const AlternativeItem = forwardRef<HTMLDivElement, React.ComponentProps<typeof AlternativeItemBase>>(
    ({ active, ...props }, ref) => <AlternativeItemBase ref={ref} active={active} {...props} />
);

const AlternativeProductsPin = ({
    alternativeProducts,
    currentAltProduct,
    onSelectAltProduct,
    style = {},
    className = '',
    showNewFlag = false,
    buildingType,
}: AlternativeProductsPinProps) => {
    const translations = useTranslations();
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const currentProduct = alternativeProducts.alternatives[currentAltProduct];

    const menuContent = (
        <AlternativeGridContent>
            {alternativeProducts.alternatives.map((alternative, index) => (
                <AlternativeItem
                    key={alternative.type + index}
                    ref={(el) => (itemRefs.current[index] = el)}
                    active={currentAltProduct === index}
                    onClick={() => onSelectAltProduct(index)}
                >
                    {translations.products[alternative.type]?.name}
                </AlternativeItem>
            ))}
        </AlternativeGridContent>
    );
    return (
        <ProductPin
            productType={currentProduct.type}
            showNewFlag={showNewFlag}
            popoverOpenDefault={false}
            style={style}
            className={className}
            customMenuContent={menuContent}
            isAlternativeProducts
            isSideBar={buildingType === BuildingType.DATA_CENTER_COOLING_AIR}
            pinCoordinates={currentProduct.position ?? undefined}
            coordinates={alternativeProducts.position}
        />
    );
};

export default AlternativeProductsPin;

